/**
 * Compendium — browse all content (cards, Programs, ICE) + lifetime stats.
 *
 * Everything is shown (this is a single-player, perfect-information game), with
 * rarity coloring. Meta unlock state is reflected where relevant (characters,
 * Heat). Acts as both a codex and a stats screen.
 */

import { el, div, button } from "../dom";
import {
  CARD_DEFS,
  PROGRAM_DEFS,
  ICE_DEFS,
  getCardDef,
} from "../../engine";
import type { Store } from "../store";

type Tab = "stats" | "cards" | "programs" | "ice";
let tab: Tab = "stats";

export function renderCompendium(store: Store): HTMLElement {
  const screen = div("screen");
  const body = div("screen__body");

  body.appendChild(
    div("row", [
      el("div", { class: "h-title", text: "COMPENDIUM" }),
      div("fill"),
      button("btn btn--sm", "◀ BACK", () => store.open("title")),
    ]),
  );

  const tabs = div("tabs");
  const mk = (t: Tab, label: string) =>
    button(`tab${tab === t ? " active" : ""}`, label, () => {
      tab = t;
      store.requestRender();
    });
  tabs.appendChild(mk("stats", "STATS"));
  tabs.appendChild(mk("cards", "CARDS"));
  tabs.appendChild(mk("programs", "PROGRAMS"));
  tabs.appendChild(mk("ice", "ICE"));
  body.appendChild(tabs);

  const content = div("scroll fill panel");
  if (tab === "stats") content.appendChild(statsView(store));
  else if (tab === "cards") content.appendChild(cardsView());
  else if (tab === "programs") content.appendChild(programsView());
  else content.appendChild(iceView());
  body.appendChild(content);

  screen.appendChild(body);
  return screen;
}

function statsView(store: Store): HTMLElement {
  const s = store.meta.stats;
  const grid = div("grid2");
  const add = (k: string, v: string | number) =>
    grid.appendChild(
      div("statline", [
        el("span", { class: "k", text: k }),
        el("span", { class: "v", text: String(v) }),
      ]),
    );
  add("Runs won", s.runsWon);
  add("Deaths", s.deaths);
  add("Abandoned", s.abandons);
  add("Best floor", s.bestFloor);
  add("Total kills", s.totalKills);
  add("Elite kills", s.totalEliteKills);
  add("Fastest win (turns)", s.fastestWinTurns ?? "—");
  add("Ghost wins", s.winsByCharacter.ghost ?? 0);
  add("Breaker wins", s.winsByCharacter.breaker ?? 0);

  const wrap = div("col");
  wrap.appendChild(grid);

  // Archetype win breakdown.
  const archEntries = Object.entries(s.winsByArchetype);
  if (archEntries.length > 0) {
    wrap.appendChild(el("div", { class: "h-sub", text: "WINS BY ARCHETYPE", style: "margin-top:8px" }));
    const ag = div("grid2");
    for (const [k, v] of archEntries.sort((a, b) => b[1] - a[1])) {
      ag.appendChild(
        div("statline", [
          el("span", { class: "k", text: k }),
          el("span", { class: "v", text: String(v) }),
        ]),
      );
    }
    wrap.appendChild(ag);
  }

  // Unlock state.
  wrap.appendChild(el("div", { class: "h-sub", text: "UNLOCKS", style: "margin-top:8px" }));
  wrap.appendChild(
    el("div", {
      class: "muted",
      text: `Characters: ${store.meta.unlockedCharacters.join(", ")}`,
    }),
  );
  return wrap;
}

function cardsView(): HTMLElement {
  const grid = div("compendium-grid");
  const ids = Object.keys(CARD_DEFS)
    .filter((id) => !CARD_DEFS[id]!.upgraded)
    .sort((a, b) => CARD_DEFS[a]!.name.localeCompare(CARD_DEFS[b]!.name));
  for (const id of ids) {
    const d = getCardDef(id);
    grid.appendChild(
      div("codex-item", [
        el("div", { class: "ci-name", text: d.name, style: `color:var(--rar-${d.rarity})` }),
        el("div", { class: "ci-meta", text: `${d.type} · ${d.cost}₵ · ${d.rarity}` }),
        el("div", {
          class: "ci-meta",
          text: d.description,
          style: "white-space:normal;color:var(--ink-mid);margin-top:2px",
        }),
      ]),
    );
  }
  return grid;
}

function programsView(): HTMLElement {
  const grid = div("compendium-grid");
  const ids = Object.keys(PROGRAM_DEFS).sort((a, b) =>
    PROGRAM_DEFS[a]!.name.localeCompare(PROGRAM_DEFS[b]!.name),
  );
  for (const id of ids) {
    const d = PROGRAM_DEFS[id]!;
    grid.appendChild(
      div("codex-item", [
        el("div", { class: "ci-name", text: d.name, style: `color:var(--rar-${d.rarity})` }),
        el("div", { class: "ci-meta", text: d.rarity }),
        el("div", {
          class: "ci-meta",
          text: d.description,
          style: "white-space:normal;color:var(--ink-mid);margin-top:2px",
        }),
      ]),
    );
  }
  return grid;
}

function iceView(): HTMLElement {
  const grid = div("compendium-grid");
  const ids = Object.keys(ICE_DEFS).sort((a, b) =>
    ICE_DEFS[a]!.name.localeCompare(ICE_DEFS[b]!.name),
  );
  for (const id of ids) {
    const d = ICE_DEFS[id]!;
    const kind = d.isBoss ? "BOSS" : d.isElite ? "ELITE" : "ICE";
    const color = d.isBoss ? "var(--red)" : d.isElite ? "var(--amber)" : "var(--ink-hi)";
    grid.appendChild(
      div("codex-item", [
        el("div", { class: "ci-name", text: d.name, style: `color:${color}` }),
        el("div", { class: "ci-meta", text: `${kind} · ${d.maxHp} HP` }),
        el("div", {
          class: "ci-meta",
          text: d.description,
          style: "white-space:normal;color:var(--ink-mid);margin-top:2px",
        }),
      ]),
    );
  }
  return grid;
}
